import { trackError } from '@iheartjane/tracking';
import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import type { DeepReadonly } from 'ts-essentials';

/* eslint-disable-next-line no-restricted-imports */
import type { BundlePossibilities } from '@jane/shared/models';

import { CartSource } from '../../sources/cart';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';

export const SHOW_BUNDLE_POSSIBILITIES = 'bundle-possibilities/show';
export const showBundlePossibilities = createAction<
  void,
  typeof SHOW_BUNDLE_POSSIBILITIES
>(SHOW_BUNDLE_POSSIBILITIES);

export const STOP_SHOW_BUNDLE_POSSIBILITIES = 'bundle-possibilities/stop-show';
export const stopShowBundlePossibilities = createAction<
  void,
  typeof STOP_SHOW_BUNDLE_POSSIBILITIES
>(STOP_SHOW_BUNDLE_POSSIBILITIES);

export const APPLY_DISCOUNTABLE_PRODUCTS_FILTER =
  'bundle-possibilities/apply-discountable-products-filter';
export const applyDiscountableProductsFilter = createAction<
  void,
  typeof APPLY_DISCOUNTABLE_PRODUCTS_FILTER
>(APPLY_DISCOUNTABLE_PRODUCTS_FILTER);

export const getBundlePossibilities =
  (cartUuid: string): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      bundlePossibilities: { touchedProductId },
    } = getState();

    if (!(touchedProductId && cartUuid)) {
      return;
    }

    return CartSource.getBundlePossibilities(cartUuid, touchedProductId)
      .then((result: BundlePossibilities) =>
        dispatch(getBundlePossibilitiesSuccess(result))
      )
      .catch((error) => {
        trackError(new Error('Bundle possibilities fetch failed'), {
          error,
        });
      });
  };

export const GET_BUNDLE_POSSIBILITIES_SUCCESS =
  'bundle-possibilities/get-success';
const getBundlePossibilitiesSuccess = createAction<
  BundlePossibilities,
  typeof GET_BUNDLE_POSSIBILITIES_SUCCESS
>(GET_BUNDLE_POSSIBILITIES_SUCCESS);

export const SET_TOUCHED_PRODUCT = 'bundle-possibilities/set-touched-product';
export const setTouchedProduct = createAction<
  string | number | undefined,
  typeof SET_TOUCHED_PRODUCT
>(SET_TOUCHED_PRODUCT);

export const CLEAR_TOUCHED_PRODUCT =
  'bundle-possibilities/clear-touched-product';
export const clearTouchedProduct = createAction<
  void,
  typeof CLEAR_TOUCHED_PRODUCT
>(CLEAR_TOUCHED_PRODUCT);

export type BundlePossibilitiesActions =
  | ReturnType<typeof showBundlePossibilities>
  | ReturnType<typeof stopShowBundlePossibilities>
  | ReturnType<typeof applyDiscountableProductsFilter>
  | ReturnType<typeof getBundlePossibilitiesSuccess>
  | ReturnType<typeof setTouchedProduct>
  | ReturnType<typeof clearTouchedProduct>;

export const defaultBundlePossibilities =
  (): BundlePossibilitiesState['bundlePossibilities'] => ({
    status: null,
    special_id: null,
    discountable_product_ids: [],
  });

export type BundlePossibilitiesState = DeepReadonly<{
  bundlePossibilities: BundlePossibilities;
  filterDiscountableProducts: boolean;
  showingBundlePossibilities: boolean;
  touchedProductId?: string | number;
}>;

const getInitialState = (): BundlePossibilitiesState => ({
  bundlePossibilities: defaultBundlePossibilities(),
  filterDiscountableProducts: false,
  showingBundlePossibilities: false,
  touchedProductId: undefined,
});

export const bundlePossibilitiesReducer: Reducer<
  BundlePossibilitiesState,
  CustomerAction
> = (state = getInitialState(), action) => {
  switch (action.type) {
    case SHOW_BUNDLE_POSSIBILITIES:
      return { ...state, showingBundlePossibilities: true };
    case STOP_SHOW_BUNDLE_POSSIBILITIES:
      return getInitialState();
    case APPLY_DISCOUNTABLE_PRODUCTS_FILTER:
      return { ...state, filterDiscountableProducts: true };
    case GET_BUNDLE_POSSIBILITIES_SUCCESS:
      return { ...state, bundlePossibilities: action.payload };
    case SET_TOUCHED_PRODUCT:
      return {
        ...state,
        bundlePossibilities: defaultBundlePossibilities(),
        touchedProductId: action.payload,
      };
    case CLEAR_TOUCHED_PRODUCT:
      return {
        ...state,
        touchedProductId: undefined,
      };
  }

  return state;
};

export const selectBundlePossibilities = (state: AppState) =>
  state.bundlePossibilities;
